import {
  useQueries,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useCallback, useEffect, useRef } from 'react';

import { fetchCostCentersBulkAdmin } from '~/data/cost-center';
import { fetchSitesBulkAdmin } from '~/data/site';

import { promiseAllThrottled, unique } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysDeliveryNote } from '../queryKeys';
import { fetchDeliveryNotesCountAdmin } from '../useQueryDeliveryNotesCountAdmin';

import { fetchDeliveryNotesAdmin } from './fetchDeliveryNotesAdmin';
import {
  type DeliveryNotesAdminQueryParameters,
  type DeliveryNotesAdminResponse,
} from './types';
import { zodDeliveryNotesAdminQueryParametersSchema } from './validations';

/**
 * Get the query options for fetching delivery notes.
 * @param options - Optional configuration for the query.
 * @param queryParameters - The search parameters for fetching the delivery notes.
 * @returns The query options.
 */
export const getDeliveryNotesAdminQueryOptions = ({
  options = {},
  queryParameters,
}: {
  options?: Omit<
    UseQueryOptions<DeliveryNotesAdminResponse>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: Partial<DeliveryNotesAdminQueryParameters>;
}): UseQueryOptions<DeliveryNotesAdminResponse> => {
  const validatedQueryParameters =
    zodDeliveryNotesAdminQueryParametersSchema.parse(queryParameters);

  return {
    queryFn: async () => fetchDeliveryNotesAdmin(validatedQueryParameters),
    queryKey: queryKeysDeliveryNote.getAll(validatedQueryParameters),
    staleTime: ms('30s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for all delivery notes that match the given filter configuration.
 * Combines the delivery notes query with the delivery notes count query and returns the paginated delivery notes data plus the total count.
 * Handles pagination and prefetches the next page of delivery notes for better performance.
 * @param queryParameters - The search parameters for fetching the delivery notes.
 * @param options - Optional configuration for the query.
 * @returns The query result containing the delivery notes data.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/search_delivery_note_vadmin_asset_delivery_note_search_post
 */
export const useQueryDeliveryNotesAdmin = (
  queryParameters: Partial<DeliveryNotesAdminQueryParameters>,
  options?: Omit<
    UseQueryOptions<DeliveryNotesAdminResponse>,
    'queryKey' | 'queryFn'
  >,
  shouldUsePrefetching = false,
) => {
  const previousDataRef = useRef<
    | {
        bulkCostCenters:
          | Awaited<ReturnType<typeof fetchCostCentersBulkAdmin>>
          | undefined;
        bulkSites: Awaited<ReturnType<typeof fetchSitesBulkAdmin>> | undefined;
        deliveryNotes: undefined;
        count: number | undefined;
      }
    | undefined
  >(undefined);

  const queryClient = useQueryClient();

  const validatedQueryParameters =
    zodDeliveryNotesAdminQueryParametersSchema.parse(queryParameters);

  // FIXME VGS-7376: remove useEffect in DeliveryList page once useQueryDeliveryNotes only consists of a single request

  const getDLNQuery = useCallback(
    (customOffset?: number) => ({
      enabled: options?.enabled !== false,
      gcTime: ms('30s'),
      placeholderData: () => previousDataRef.current?.deliveryNotes,
      async queryFn({ signal }: { signal: AbortSignal }) {
        try {
          const response = await fetchDeliveryNotesAdmin(
            {
              ...validatedQueryParameters,
              offset: customOffset ?? validatedQueryParameters.offset ?? 0,
            },
            { signal },
          );

          const allPermittedCostCenterIds = response?.assets?.length
            ? unique(
                response.assets
                  .flatMap((asset) => asset.sgw ?? [])
                  .filter(({ type }) => type === 'cc')
                  .map(({ typeId }) => typeId),
              ).filter(Boolean)
            : undefined;

          const allPermittedToSiteIds = response?.assets?.length
            ? unique(
                response.assets
                  .flatMap((asset) => asset.sgw ?? [])
                  .filter(({ type }) => type === 'pr')
                  .map(({ typeId }) => typeId),
              ).filter(Boolean)
            : undefined;

          const promiseMap = new Map<string, Promise<any>>();

          if (allPermittedCostCenterIds?.length) {
            promiseMap.set(
              'costCenters',
              fetchCostCentersBulkAdmin(allPermittedCostCenterIds, { signal }),
            );
          }

          if (allPermittedToSiteIds?.length) {
            promiseMap.set(
              'sites',
              fetchSitesBulkAdmin(allPermittedToSiteIds, { signal }),
            );
          }

          const promiseResults = await Promise.allSettled(promiseMap.values());

          const resultMap = new Map(
            [...promiseMap.keys()].map((key, index) => [
              key,
              promiseResults[index],
            ]),
          );

          const bulkCostCenters = resultMap.get('costCenters')?.value ?? [];
          const bulkSites = resultMap.get('sites')?.value ?? [];

          previousDataRef.current = {
            ...previousDataRef.current,
            bulkCostCenters,
            bulkSites,
            deliveryNotes: response,
          };

          return {
            ...response,
            bulkCostCenters,
            bulkSites,
          };
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            // Re-throw AbortError without logging as it's expected when requests are cancelled
            throw error;
          }

          log.error({
            message: 'Error fetching delivery notes and related data',
            error,
          });

          // Re-throw so the query lands in its error state. Swallowing it here
          // rendered an empty table for a failed list, which reads like "no
          // delivery notes match" instead of "the request failed".
          throw error;
        }
      },
      queryKey: queryKeysDeliveryNote.getAll({
        ...validatedQueryParameters,
        offset: customOffset ?? validatedQueryParameters.offset,
      }),
      staleTime: ms('10s'),
    }),
    [queryParameters, options?.enabled],
  );

  const getDLNCountQuery = useCallback(() => {
    const countQueryParams = {
      filterConfig: validatedQueryParameters?.filterConfig,
    };

    return {
      enabled: options?.enabled !== false,
      placeholderData: () => previousDataRef.current?.count,
      async queryFn({ signal }: { signal: AbortSignal }) {
        try {
          const { totalAssets: count } = await fetchDeliveryNotesCountAdmin(
            countQueryParams,
            { signal },
          );

          previousDataRef.current = {
            ...previousDataRef.current,
            count,
          };

          return count;
        } catch (error) {
          // Drop the remembered count so the placeholder cannot show the
          // previous filter's total next to the current filter's rows.
          if (previousDataRef.current) {
            previousDataRef.current.count = undefined;
          }

          throw error;
        }
      },
      queryKey: queryKeysDeliveryNote.getCount(countQueryParams),
    };
  }, [queryParameters?.filterConfig, options?.enabled]);

  const queries = useQueries({ queries: [getDLNQuery(), getDLNCountQuery()] });

  const [deliveryNotesQuery, deliveryNotesCountQuery] = queries;

  const isLoading = queries.some(({ isLoading }) => isLoading);
  const isFetching = queries.some(({ isFetching }) => isFetching);
  // Only the list decides whether the page failed. The total is supplementary:
  // the vadmin count endpoint rejects filters it cannot serve from an index
  // (COR-577), and a rejected total must cost the total, not the whole list.
  const isError = deliveryNotesQuery.isError;
  const isSuccess = deliveryNotesQuery.isSuccess;

  let data = {
    assets: deliveryNotesQuery.data?.assets ?? [],
    bulkCostCenters: deliveryNotesQuery.data?.bulkCostCenters ?? [],
    bulkSites: deliveryNotesQuery.data?.bulkSites ?? [],
    hasNextPage: deliveryNotesQuery.data?.hasNextPage,
    paginatedCount: deliveryNotesQuery.data?.paginatedCount,
    totalCount: deliveryNotesCountQuery.data,
  };
  if (options?.select && data.assets?.length) {
    data = options.select(data);
  }

  const refetch = async () => {
    await promiseAllThrottled(queries.map(async (query) => query.refetch()));
  };

  useEffect(() => {
    if (shouldUsePrefetching) {
      try {
        const { limit, offset } = validatedQueryParameters;

        const totalCount = Number(data?.totalCount);

        if (!Number.isFinite(totalCount)) {
          return;
        }

        // Prefetch next page if there are more items
        if (offset + limit < totalCount) {
          void queryClient.prefetchQuery(getDLNQuery(offset + limit));
        }

        // Prefetch previous page if we're not at the start
        if (offset - limit >= 0) {
          void queryClient.prefetchQuery(getDLNQuery(offset - limit));
        }
      } catch {
        log.warn({
          message: 'Invalid search parameters for prefetching',
          data: { queryParameters },
        });
      }
    }
  }, [queryParameters.limit, queryParameters.offset, data?.totalCount]);

  return {
    data,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  };
};
